import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, FileText, History, Lock, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

import { api, ApiError } from "@/api";
import { useAuth } from "@/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ListFilterBar from "@/components/ListFilterBar";
import DocumentNumberLink from "@/components/DocumentNumberLink";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import {
  isWithinDateRange,
  useClearFilterParams,
  useFilterParam,
} from "@/hooks/useFilterParam";
import { formatNumber } from "@/lib/format";
import { coerceQuoteStatus, quoteStatusLabel } from "@/lib/quoteStatus";

interface QuotationRow {
  id: number;
  quote_number: string;
  number?: string;
  customer_name: string;
  status: string;
  total_amount: number;
  total?: number;
  pfi_date: string | null;
  locked_by_waybill: boolean;
  // SUM of live receipts against this quote — drives the cancel gate.
  total_paid?: number;
  has_payment?: boolean;
}

interface QuotationCancellation {
  id: number;
  quote_number: string;
  customer_name: string;
  status: string;
  total_amount: number;
  cancellation_reason: string;
  cancelled_by: string;
  cancelled_at: string | null;
}

function statusBadgeClass(status: string) {
  if (status === "approved" || status === "paid") {
    return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
  }
  if (status === "pending" || status === "partial") {
    return "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30";
  }
  return "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30";
}

const FILTER_KEYS = ["q", "doc_no", "customer", "status", "date_from", "date_to"] as const;

const PAID_TOOLTIP = "Cannot cancel — payment has been received";

export default function Quotations() {
  const nav = useNavigate();
  const { can } = useAuth();
  const canEdit = can("quotes.write");
  const canCancel = can("quotes.cancel");
  const canRead = can("quotes.read");

  const [rows, setRows] = useState<QuotationRow[]>([]);
  const [cancellations, setCancellations] = useState<QuotationCancellation[]>(
    [],
  );
  const [search, setSearch] = useFilterParam("q");
  const [docNo, setDocNo] = useFilterParam("doc_no");
  const [customerQuery, setCustomerQuery] = useFilterParam("customer");
  const [statusFilter, setStatusFilter] = useFilterParam("status");
  const [dateFrom, setDateFrom] = useFilterParam("date_from");
  const [dateTo, setDateTo] = useFilterParam("date_to");
  const [tab, setTab] = useState<"active" | "cancelled">("active");
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const clearFilters = useClearFilterParams(FILTER_KEYS);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<QuotationRow | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Distinct customer names from the loaded result set. Derived list keeps
  // the dropdown perfectly aligned with what the user is allowed to see —
  // no extra API calls, no permission leaks.
  const customerOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of rows) if (r.customer_name?.trim()) names.add(r.customer_name);
    for (const c of cancellations) {
      if (c.customer_name?.trim()) names.add(c.customer_name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows, cancellations]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    const docNeedle = docNo.toLowerCase().trim();
    const custNeedle = customerQuery.toLowerCase().trim();
    return rows.filter((q) => {
      const number = (q.quote_number || q.number || "").toLowerCase();
      const cust = (q.customer_name || "").toLowerCase();
      const searchMatch =
        !s || number.includes(s) || cust.includes(s);
      const docMatch = !docNeedle || number.includes(docNeedle);
      const custMatch = !custNeedle || cust.includes(custNeedle);
      const statusMatch =
        !statusFilter || statusFilter === "all"
          ? true
          : coerceQuoteStatus(q.status) === statusFilter;
      const dateMatch = isWithinDateRange(q.pfi_date, dateFrom, dateTo);
      return searchMatch && docMatch && custMatch && statusMatch && dateMatch;
    });
  }, [rows, search, docNo, customerQuery, statusFilter, dateFrom, dateTo]);

  const filteredCancelled = useMemo(() => {
    const s = search.toLowerCase().trim();
    const docNeedle = docNo.toLowerCase().trim();
    const custNeedle = customerQuery.toLowerCase().trim();
    return cancellations.filter((c) => {
      const number = (c.quote_number || "").toLowerCase();
      const cust = (c.customer_name || "").toLowerCase();
      const searchMatch = !s || number.includes(s) || cust.includes(s);
      const docMatch = !docNeedle || number.includes(docNeedle);
      const custMatch = !custNeedle || cust.includes(custNeedle);
      const statusMatch =
        !statusFilter || statusFilter === "all"
          ? true
          : c.status === statusFilter;
      return searchMatch && docMatch && custMatch && statusMatch;
    });
  }, [cancellations, search, docNo, customerQuery, statusFilter]);

  const hasAnyFilter = Boolean(
    search ||
      docNo ||
      customerQuery ||
      (statusFilter && statusFilter !== "all") ||
      dateFrom ||
      dateTo,
  );

  const activePg = usePagination(filtered);
  const cancelledPg = usePagination(filteredCancelled);

  useEffect(() => {
    activePg.setPage(1);
    cancelledPg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, docNo, customerQuery, statusFilter, dateFrom, dateTo]);

  async function loadActive() {
    setLoading(true);
    try {
      const res = await api.get<{ quotations: QuotationRow[] }>("/quotations");
      setRows(res.quotations ?? []);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load quotations");
    } finally {
      setLoading(false);
    }
  }

  async function loadCancellations() {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await api.get<{ cancellations: QuotationCancellation[] }>(
        "/quotations/cancellations/history",
      );
      setCancellations(res.cancellations ?? []);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Failed to load cancellations";
      setHistoryError(msg);
      setCancellations([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    loadActive();
    loadCancellations();
  }, []);

  function openCancelDialog(q: QuotationRow) {
    setCancelTarget(q);
    setCancelReason("");
    setCancelOpen(true);
  }

  async function confirmCancel() {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) {
      toast.error("Please enter a reason for cancellation");
      return;
    }
    setCancelling(true);
    try {
      await api.del(`/quotations/${cancelTarget.id}`, {
        reason: cancelReason.trim(),
      });
      toast.success("Quotation cancelled");
      setCancelOpen(false);
      setCancelTarget(null);
      setCancelReason("");
      setTab("cancelled");
      await Promise.all([loadActive(), loadCancellations()]);
    } catch (err) {
      toast.error((err as Error).message || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Quotations</h1>
          <p className="text-muted-foreground">
            All quotations created by your team.
          </p>
        </div>
        {canEdit ? (
          <Button
            onClick={() => nav("/quotations/new")}
            className="bg-violet-500 hover:bg-violet-400 text-white rounded-xl px-5"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Quotation
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
        <ListFilterBar
          title="Search Quotation"
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search quotations..."
          docNo={{
            value: docNo,
            onChange: setDocNo,
            placeholder: "Quotation No.",
          }}
          customer={{
            value: customerQuery,
            onChange: setCustomerQuery,
            options: customerOptions,
            placeholder: "Customer",
          }}
          status={{
            value: statusFilter || "all",
            onChange: (v) => setStatusFilter(v === "all" ? "" : v),
            options: [
              { value: "all", label: "All statuses" },
              { value: "draft", label: "Draft" },
              { value: "approved", label: "Approved" },
            ],
          }}
          dateFrom={{ value: dateFrom, onChange: setDateFrom }}
          dateTo={{ value: dateTo, onChange: setDateTo }}
          hasAnyFilter={hasAnyFilter}
          onClear={clearFilters}
        />
        <Card className="shadow-card">
          <Tabs
            value={tab}
            onValueChange={(v) => setTab(v as "active" | "cancelled")}
            className="w-full"
          >
            <div className="px-4 pt-3 border-b">
              <TabsList>
                <TabsTrigger value="active">Active ({rows.length})</TabsTrigger>
                <TabsTrigger value="cancelled" className="gap-1.5">
                  <History className="h-3.5 w-3.5" />
                  Cancelled ({cancellations.length})
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="active" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Quotation No.</TableHead>
                    <TableHead>Customer Name</TableHead>
                    <TableHead>PFI Date</TableHead>
                    <TableHead className="text-right">Total (GHS)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-muted-foreground py-10"
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {!loading && filtered.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-muted-foreground py-10"
                      >
                        No quotations.
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {!loading &&
                    activePg.pageItems.map((q, i) => {
                      const number = q.quote_number || q.number || "";
                      const totalValue = Number(q.total_amount ?? q.total ?? 0);
                      const pfi = q.pfi_date
                        ? new Date(q.pfi_date).toLocaleDateString()
                        : "—";
                      const paidSoFar = Number(q.total_paid ?? 0);
                      const hasPayment =
                        q.has_payment === true || paidSoFar > 0;
                      // Payment state is derived from receipts, never from the
                      // quote workflow status (which is draft/approved only).
                      const fullyPaid =
                        totalValue > 0 && paidSoFar >= totalValue - 0.01;
                      const workflowStatus = coerceQuoteStatus(q.status);
                      return (
                        <TableRow key={q.id}>
                          <TableCell className="text-muted-foreground">
                            {activePg.from + i}
                          </TableCell>
                          <TableCell className="font-mono">
                            <DocumentNumberLink
                              to={`/quotations/${q.id}`}
                              canOpen={canRead}
                            >
                              {number}
                            </DocumentNumberLink>
                          </TableCell>
                          <TableCell className="font-semibold">
                            {q.customer_name || "—"}
                          </TableCell>
                          <TableCell>{pfi}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatNumber(totalValue)}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span
                                className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs capitalize ${statusBadgeClass(workflowStatus)}`}
                              >
                                {quoteStatusLabel(workflowStatus)}
                              </span>
                              {hasPayment ? (
                                <span className="inline-flex items-center rounded-full border border-cyan-500/40 bg-cyan-500/10 px-2 py-0.5 text-[10px] text-cyan-700 dark:text-cyan-300">
                                  {fullyPaid ? "Paid" : "Part-paid"}
                                </span>
                              ) : null}
                              {q.locked_by_waybill ? (
                                <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-700 dark:text-amber-300">
                                  <Lock className="h-3 w-3" />
                                  Waybill
                                </span>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="inline-flex items-center gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() =>
                                  nav(`/quotations/${q.id}?openPdf=1`)
                                }
                                title="Preview PDF"
                              >
                                <FileText className="h-4 w-4 text-primary" />
                              </Button>
                              {!q.locked_by_waybill ? (
                                <>
                                  {canEdit ? (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() =>
                                        nav(`/quotations/${q.id}/edit`)
                                      }
                                      title="Edit"
                                    >
                                      <Pencil className="h-4 w-4 text-primary" />
                                    </Button>
                                  ) : null}
                                  {canCancel ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex">
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => openCancelDialog(q)}
                                            disabled={hasPayment}
                                            title={
                                              hasPayment
                                                ? PAID_TOOLTIP
                                                : "Cancel quotation"
                                            }
                                          >
                                            <Ban className="h-4 w-4 text-destructive" />
                                          </Button>
                                        </span>
                                      </TooltipTrigger>
                                      {hasPayment ? (
                                        <TooltipContent>
                                          {PAID_TOOLTIP}
                                        </TooltipContent>
                                      ) : null}
                                    </Tooltip>
                                  ) : null}
                                </>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
              {!loading && filtered.length > 0 ? (
                <Pagination
                  page={activePg.page}
                  pageCount={activePg.pageCount}
                  total={activePg.total}
                  from={activePg.from}
                  to={activePg.to}
                  onPrev={activePg.prev}
                  onNext={activePg.next}
                />
              ) : null}
            </TabsContent>

            <TabsContent value="cancelled" className="mt-0">
              {historyError ? (
                <div className="px-4 py-6 text-center text-sm text-destructive">
                  {historyError}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 ml-3"
                    onClick={loadCancellations}
                  >
                    Retry
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quotation No.</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead className="text-right">Total (GHS)</TableHead>
                      <TableHead>Cancelled By</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground py-10"
                        >
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {!historyLoading && filteredCancelled.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground py-10"
                        >
                          No cancelled quotations yet.
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {!historyLoading &&
                      cancelledPg.pageItems.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-mono">
                            {c.quote_number || "—"}
                          </TableCell>
                          <TableCell>{c.customer_name || "—"}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatNumber(Number(c.total_amount ?? 0))}
                          </TableCell>
                          <TableCell>{c.cancelled_by}</TableCell>
                          <TableCell className="max-w-[240px] text-sm whitespace-pre-wrap">
                            {c.cancellation_reason}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
              {!historyError &&
              !historyLoading &&
              filteredCancelled.length > 0 ? (
                <Pagination
                  page={cancelledPg.page}
                  pageCount={cancelledPg.pageCount}
                  total={cancelledPg.total}
                  from={cancelledPg.from}
                  to={cancelledPg.to}
                  onPrev={cancelledPg.prev}
                  onNext={cancelledPg.next}
                />
              ) : null}
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Quotation</DialogTitle>
          </DialogHeader>
          {cancelTarget ? (
            <p className="text-sm text-muted-foreground">
              Cancelling{" "}
              <span className="font-mono text-foreground">
                {cancelTarget.quote_number || cancelTarget.number}
              </span>
              {cancelTarget.customer_name
                ? ` — ${cancelTarget.customer_name}`
                : ""}
            </p>
          ) : null}
          <div className="space-y-1.5">
            <Label>Reason for cancellation *</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              placeholder="Enter why this quotation is being cancelled..."
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={cancelling}
            >
              Close
            </Button>
            <Button
              onClick={confirmCancel}
              disabled={cancelling}
              variant="destructive"
            >
              {cancelling ? "Cancelling..." : "Confirm Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
